// TODO: выпилить после переезда на вертикали из ручки формы в проде
import {yupResolver} from '@hookform/resolvers/yup'
import {FullscreenOverlayWithSpinner} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {type ReactElement, useCallback, useEffect, useMemo, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {array, lazy, object, string} from 'yup'

import {LayoutSkeleton} from './components/LayoutSkeleton'
import {ActionProvider} from './contexts/ActionContext'
import {ConfigProvider} from './contexts/ConfigContext'
import {StatusProvider} from './contexts/StatusContext'
import {FieldName, routeDestinationPrefixName, routeReturnName, routeSourceName} from './lib/fieldNames'
import * as classes from './OrderForm@mobile.classes'
import type {AppConfig, OrderFormState} from './types'
import {renderWidget} from './widgets'
import type {WidgetConfig} from './widgets/types'

export type OrderFormProps = {
  /**
   * Дефолтное состояние формы
   */
  defaultValues?: OrderFormState
  /**
   * Блокирование возможности отправить форму
   */
  disableSubmit?: boolean
  /**
   * Любая загрузка формы
   */
  isFetching: boolean
  /**
   * Корневой виджет формы
   */
  layout?: WidgetConfig
  /**
   * Первая загрузка формы
   */
  isLoading?: boolean
  /**
   * Нужно ли показывать оверлей, блокирующий взаимодействие с формой.
   */
  isOverlayVisible: boolean
  /**
   * Изменение любого поля формы
   * @param data Состояние формы
   * @returns void
   */
  onChange?: (data: OrderFormState) => void
  /**
   * Конфиг приложения на минималках
   */
  config: AppConfig
  /**
   * Коллбэк на сабмит формы
   * @param data Стейт формы
   */
  onSubmit: (data: OrderFormState) => void
  outerDefaults?: OrderFormState
}

export const OrderForm = ({
  defaultValues,
  layout,
  isLoading,
  isFetching,
  isOverlayVisible,
  onChange,
  config,
  onSubmit,
  outerDefaults,
}: OrderFormProps): ReactElement => {
  const i18n = useI18n()
  const [nddOffer, setNddOffer] = useState<string | undefined>(undefined)
  const resolver = useMemo(
    () =>
      yupResolver(
        getFormValidationSchema(outerDefaults?.taxi_class ?? '', i18n, config),
        {},
        {
          mode: 'async',
        },
      ),
    [outerDefaults?.taxi_class, config, i18n],
  )

  const form = useForm<OrderFormState>({
    defaultValues,
    resolver,
    mode: 'onBlur',
  })

  const handleChange = useCallback(
    // скидываем поле ndd-оффера на изменение стейта формы
    (data: OrderFormState): void => {
      const offer = data.ndd_offer_name
      if (offer) {
        if (offer === nddOffer || data.vertical_name !== 'ndd_self_pickup') {
          setNddOffer(undefined)
          form.setValue('ndd_offer_name', undefined)
          data.ndd_offer_name = undefined
        } else {
          setNddOffer(offer)
        }
      }
      onChange?.(data)
    },
    [form, nddOffer, onChange],
  )

  useEffect(() => {
    if (onChange) {
      return form.watch(handleChange).unsubscribe
    }
  }, [form, handleChange, onChange])

  if (!isLoading && !layout) {
    throw new Error('Root form widget is not found')
  }

  /** ресет формы для обновления стейта при смене внешних дефолтов
   * более адекватного способа внешнего управления стейтом в RHF я не нашла
   */
  useEffect(() => {
    if (outerDefaults) {
      form.reset(formValues => ({...formValues, ...outerDefaults}))
    }
  }, [form, onChange, outerDefaults])

  return (
    <StatusProvider isFetching={isFetching}>
      <ActionProvider onSubmit={onSubmit}>
        <ConfigProvider {...config}>
          <FormProvider {...form}>
            {isOverlayVisible && <FullscreenOverlayWithSpinner />}
            <main className={classes.main}>{layout ? renderWidget(layout, 'layout') : <LayoutSkeleton />}</main>
          </FormProvider>
        </ConfigProvider>
      </ActionProvider>
    </StatusProvider>
  )
}

export const getFormValidationSchema = (taxiClass: string, i18n: (key: string) => string, config: AppConfig) => {
  const validationSchemas = {
    ndd_self_pickup: object({
      [FieldName.Ndd]: object({
        route: object({
          source: object({
            pickupPoint: object().required(i18n('validation.required')),
            phone: string()
              .required(i18n('validation.required'))
              .test('phone', i18n('validation.phone.invalid'), config.checkIsValidPhone),
          }),
          destination: object({
            pickupPoint: object().required(i18n('validation.required')),
            phone: string()
              .required(i18n('validation.required'))
              .test('phone', i18n('validation.phone.invalid'), config.checkIsValidPhone),
          }),
        }),
      }),
    }),
    DEFAULT: object({
      [FieldName.Route]: lazy(obj =>
        object({
          ...Object.keys(obj || {}).reduce((acc, key) => {
            acc[key] = key.startsWith(routeDestinationPrefixName) // адреса итерируются через значение в ключе: destination_${iteration}
              ? object({
                  address: object({
                    title: string().required(i18n('validation.required')),
                  }),
                })
              : undefined

            return acc
          }, {}),
          [routeSourceName]: object({
            address: object({
              title: string().required(i18n('validation.required')),
            }),
          }),
          [routeReturnName]: object({
            address: object({
              title: string(),
              // валидируем телефон, только если выбран адрес
              phone: string().when('title', {
                is: (title?: string) => {
                  return typeof title === 'string' && title.length > 0
                },
                then: string()
                  .required(i18n('validation.required'))
                  .test('phone', i18n('validation.phone.invalid'), config.checkIsValidPhone),
              }),
            }),
          }),
        }),
      ),
      [FieldName.Contacts]: array(
        object({
          phone: string()
            .required(i18n('validation.required'))
            .test('phone', i18n('validation.phone.invalid'), config.checkIsValidPhone),
        }),
      )
        .default([{phone: ''}, {phone: ''}])
        .required(i18n('validation.required')),
    }),
  }

  return validationSchemas[taxiClass] ?? validationSchemas.DEFAULT
}
